import { fetchMediaBank } from '../requests/webMedia';
import { normalizeMediaBank } from '../../shared/media/mediaBank';

/**
 * Registre module-level de la banque de médias.
 *
 * Même forme que `variableCatalogRegistry` et pour la même raison : il vit
 * **hors** du store Puck (invariant §4.3 du README). Les champs de la sidebar
 * le lisent par `useSyncExternalStore` ; rien ne remonte d'état dans un parent
 * du canvas, donc ouvrir la banque ne pèse pas sur la frappe.
 *
 * Chargement **paresseux** : aucun appel à l'ouverture de l'éditeur, la banque
 * n'est lue qu'à la première ouverture de l'onglet « Banque ».
 *
 * La source est la même que celle du menu `@` : `projectConfig.documentIdentity`
 * pour un site de proposition, le web_template pour un modèle de site. C'est le
 * back qui borne le périmètre au métier du document (concern `MediaBankServing`),
 * le front ne filtre rien.
 */

const EMPTY_FOLDERS = [];

let source = null;
let folders = EMPTY_FOLDERS;
let status = 'idle';
let error = null;
let inFlight = null;
let snapshot = { folders: EMPTY_FOLDERS, status: 'idle', error: null };

const listeners = new Set();

function emit() {
  snapshot = { folders, status, error };
  listeners.forEach((listener) => listener());
}

/** Identité du document qui fournit la banque : `{ id, type }`. */
export function setMediaBankSource(next) {
  const nextId = next?.id != null ? String(next.id) : null;
  const nextType = next?.type || null;
  if (source?.id === nextId && source?.type === nextType) return;
  source = nextId && nextType ? { id: nextId, type: nextType } : null;
  folders = EMPTY_FOLDERS;
  status = 'idle';
  error = null;
  inFlight = null;
  emit();
}

export function getMediaBankSource() {
  return source;
}

export function subscribeMediaBank(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getMediaBankSnapshot() {
  return snapshot;
}

/**
 * Charge la banque (une seule requête en vol à la fois).
 *
 * On passe par `fetchMediaBank` et non par `getMediaBank`, qui avale ses
 * erreurs et rend `[]` : une banque vide et un endpoint absent ne se disent pas
 * pareil à l'utilisateur. Sans cette distinction, un modèle de site dont
 * l'action `media_bank` n'existe pas encore annonçait « aucun média dans la
 * banque de votre entreprise » — un mensonge, et un faux diagnostic.
 */
export function loadMediaBank({ force = false } = {}) {
  if (!source) {
    if (status !== 'unavailable') {
      status = 'unavailable';
      error = null;
      emit();
    }
    return Promise.resolve(EMPTY_FOLDERS);
  }
  if (!force && status === 'ready') return Promise.resolve(folders);
  if (inFlight && !force) return inFlight;

  const requested = source;
  status = 'loading';
  error = null;
  emit();

  inFlight = fetchMediaBank(requested)
    .then((raw) => {
      // Requête d'un document précédent : son résultat ne doit pas s'installer.
      if (requested !== source) return folders;
      folders = normalizeMediaBank(raw);
      status = 'ready';
      error = null;
      emit();
      return folders;
    })
    .catch((e) => {
      if (requested !== source) return folders;
      console.error('[webviewer] chargement de la banque de médias', requested, e);
      status = 'error';
      error = e;
      emit();
      return EMPTY_FOLDERS;
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
}

/**
 * Invalide le cache après un upload : le nouveau média est rangé dans le
 * dossier auto du document (`origin: editor`), il doit apparaître dans la
 * banque sans recharger l'éditeur. Équivalent de `mediaBankVersion` dans le
 * store de l'éditeur classique.
 */
export function invalidateMediaBank() {
  if (status === 'idle' || status === 'unavailable') return;
  status = 'idle';
  inFlight = null;
  emit();
}

/** Remise à zéro complète — réservée aux tests. */
export function resetMediaBankRegistry() {
  source = null;
  folders = EMPTY_FOLDERS;
  status = 'idle';
  error = null;
  inFlight = null;
  snapshot = { folders: EMPTY_FOLDERS, status: 'idle', error: null };
  listeners.clear();
}
